import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import log from 'loglevel';
import type { S3FileItem } from '@/types/transferFile';
import type { FileManagerConfig } from '@/types/fileManager';
import { TransferFileService } from '@/service/TransferFileService';
import { useFileManagerAuth } from './useFileManagerAuth';

export interface UseTransferFilesReturn {
  files: S3FileItem[];
  folders: string[];
  currentPath: string;
  loading: boolean;
  error: string | null;
  uploadingFile: File | null;
  uploadProgress: number;
  uploadMessage: string | null;
  loadFiles: (path?: string) => Promise<void>;
  handleUpload: (file: File, destinationPath?: string) => Promise<void>;
  handleDelete: (key: string) => Promise<void>;
  handleDownload: (key: string) => Promise<void>;
  navigateToFolder: (folderName: string) => void;
  navigateUp: () => void;
}

type FileManagerUser = NonNullable<
  ReturnType<typeof useFileManagerAuth>['user']
>;

type ListFilesResult = {
  files: S3FileItem[];
  folders: string[];
};

/**
 * Hook for managing S3 file operations: listing, uploading, deleting,
 * downloading, and navigating the virtual folder structure.
 *
 * - Files are listed directly from the S3 active prefix
 * - Uploads go to the upload-staging prefix (backend moves to active)
 * - Delete moves files from active to archive prefix
 *
 * NOTE (future): the "processing -> active" transition could be driven by an AppSync subscription
 * once the backend publishes completion events. For now we poll listFiles after upload.
 */
export function useTransferFiles(
  config: FileManagerConfig
): UseTransferFilesReturn {
  const [files, setFiles] = useState<S3FileItem[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);

  const service = useMemo(() => new TransferFileService(config), [config]);
  const { user } = useFileManagerAuth();

  // Refs to keep callbacks stable and avoid dependency-driven re-fetches
  const currentPathRef = useRef(currentPath);
  currentPathRef.current = currentPath;

  const serviceRef = useRef(service);
  serviceRef.current = service;

  const userRef = useRef(user);
  userRef.current = user;

  /** Load files and folders for a given path. Stable reference — never changes. */
  const loadFiles = useCallback(async (path?: string) => {
    const resolvedPath = path ?? currentPathRef.current;
    const currentUser = userRef.current;

    if (!currentUser) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = (await serviceRef.current.listFiles(
        resolvedPath,
        currentUser
      )) as ListFilesResult;

      setFiles(result.files);
      setFolders(result.folders);

      // Normalize: ensure trailing slash for non-empty paths, empty string for root
      const normalizedPath = resolvedPath.replace(/\/+$/g, '');
      const newPath = normalizedPath ? `${normalizedPath}/` : '';
      setCurrentPath(newPath);
      currentPathRef.current = newPath;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load files';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFilesRef = useRef(loadFiles);
  loadFilesRef.current = loadFiles;

  // Upload completion polling
  const pollCancelRef = useRef<{ cancelled: boolean }>({ cancelled: false });
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPoll = useCallback(() => {
    pollCancelRef.current.cancelled = true;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => cancelPoll, [cancelPoll]);

  function sleep(ms: number): Promise<void> {
    return new Promise(resolve => {
      pollTimerRef.current = setTimeout(resolve, ms);
    });
  }

  /**
   * Poll listFiles until the uploaded file transitions to status=active.
   * - Avoids toggling the global "loading" state by calling service.listFiles directly.
   * - Refreshes the UI once on success by calling loadFiles(path).
   */
  const pollUntilActive = useCallback(
    async (fileName: string, path: string, currentUser: FileManagerUser) => {
      cancelPoll();
      pollCancelRef.current = { cancelled: false };

      const startedAt = Date.now();
      const maxMs = 5 * 60 * 1000; // 5 minutes
      let delayMs = 2000; // start at 2s
      const maxDelayMs = 15000; // cap at 15s

      log.info('Polling for upload completion', { fileName, path });

      while (
        !pollCancelRef.current.cancelled &&
        Date.now() - startedAt < maxMs
      ) {
        await sleep(delayMs);
        if (pollCancelRef.current.cancelled) return;

        try {
          const snapshot = (await serviceRef.current.listFiles(
            path,
            currentUser
          )) as ListFilesResult;

          const match = snapshot.files.find(f => f.name === fileName);
          const isActive = match?.status === 'active';

          if (isActive) {
            log.info('Upload completed (file is active)', { fileName, path });
            setUploadMessage('Upload complete');
            await loadFilesRef.current?.(path); // refresh UI once
            return;
          }
        } catch (err) {
          // Transient errors shouldn't fail the upload; keep polling.
          log.debug('pollUntilActive listFiles failed', err);
        }

        // simple backoff
        delayMs = Math.min(Math.round(delayMs * 1.5), maxDelayMs);
      }

      if (!pollCancelRef.current.cancelled) {
        log.warn('Timed out waiting for file to become active', {
          fileName,
          path,
        });
        setUploadMessage(
          'Upload is still processing (refresh to check status)'
        );
      }
    },
    [cancelPoll]
  );

  /** Upload a file to the upload-staging prefix. */
  const handleUpload = useCallback(
    async (file: File, destinationPath?: string) => {
      const currentUser = userRef.current;

      if (!currentUser) {
        setError('User not authenticated');
        return;
      }

      const uploadPath =
        destinationPath !== undefined
          ? destinationPath
          : currentPathRef.current;

      setUploadingFile(file);
      setUploadProgress(0);
      setUploadMessage(null);
      setError(null);

      try {
        const result = await serviceRef.current.uploadFile(
          file,
          uploadPath,
          currentUser,
          setUploadProgress
        );

        setUploadMessage(result.message);

        // Show the processing item ASAP (upload-staging listing)
        await loadFilesRef.current?.(uploadPath);

        // Poll until backend finishes moving file into active
        void pollUntilActive(file.name, uploadPath, currentUser);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Upload failed';
        setError(errorMessage);
      } finally {
        setUploadingFile(null);
      }
    },
    [pollUntilActive]
  );

  /** Delete a file by moving it from active to archive prefix, then refresh the list. */
  const handleDelete = useCallback(async (key: string) => {
    const currentUser = userRef.current;

    if (!currentUser) {
      setError('User not authenticated');
      return;
    }

    setError(null);

    try {
      await serviceRef.current.deleteFile(key, currentUser);
      await loadFilesRef.current?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Delete failed';
      setError(errorMessage);
    }
  }, []);

  /** Get a presigned download URL and open it in a new tab. */
  const handleDownload = useCallback(async (key: string) => {
    const currentUser = userRef.current;

    if (!currentUser) {
      setError('User not authenticated');
      return;
    }

    setError(null);

    try {
      const url = await serviceRef.current.getDownloadUrl(key, currentUser);
      window.open(url, '_blank');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Download failed';
      setError(errorMessage);
    }
  }, []);

  /** Navigate into a subfolder. */
  const navigateToFolder = useCallback((folderName: string) => {
    const cp = currentPathRef.current;
    const newPath = cp ? `${cp}${folderName}/` : `${folderName}/`;
    loadFilesRef.current?.(newPath);
  }, []);

  /** Navigate up one level in the folder hierarchy. */
  const navigateUp = useCallback(() => {
    const cp = currentPathRef.current;
    if (!cp) return;

    const parts = cp.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length ? `${parts.join('/')}/` : '';
    loadFilesRef.current?.(newPath);
  }, []);

  return {
    files,
    folders,
    currentPath,
    loading,
    error,
    uploadingFile,
    uploadProgress,
    uploadMessage,
    loadFiles,
    handleUpload,
    handleDelete,
    handleDownload,
    navigateToFolder,
    navigateUp,
  };
}
